import { KeyValue, KeyValuePipe, NgClass } from '@angular/common';
import { Component, effect, input } from '@angular/core';
import { FormGroup, ReactiveFormsModule } from '@angular/forms';
import { MatCard } from '@angular/material/card';
import { MatSliderModule } from '@angular/material/slider';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { startWith } from 'rxjs';
import { FormMode } from '../../models';
import {
  ComputedField,
  FieldDefinition,
  ScaleDefinition,
} from '../../models/dynamic-scale.model';
import { DynamicScaleCalculator } from './dynamic-scale-calculator';

@UntilDestroy()
@Component({
  selector: 'siat-dynamic-scale',
  templateUrl: './dynamic-scale.component.html',
  imports: [
    MatSliderModule,
    ReactiveFormsModule,
    MatCard,
    NgClass,
    KeyValuePipe,
  ],
})
export class DynamicScaleComponent {
  /**
   * The parent form group.
   */
  parentForm = input.required<FormGroup>();

  /**
   * The scale configuration data.
   */
  scaleData = input.required<ScaleDefinition>();

  /**
   * The current form mode (filling, readonly, etc.).
   */
  formMode = input.required<FormMode>();

  /**
   * @returns {FormGroup} The specific form group for the scale fields.
   */
  get scaleForm(): FormGroup {
    return this.parentForm().get('value') as FormGroup;
  }

  /**
   * @returns {number} The total score based on the configured scoreKey.
   */
  get totalScore(): number {
    return (
      this.scaleForm?.get(this.scaleData()?.scoreConfig.scoreKey)?.value || 0
    );
  }

  /**
   * @returns {ComputedField | undefined} The computed field representing the main total score.
   */
  get totalComputedField(): ComputedField | undefined {
    return this.scaleData()?.computedFields.find(
      f => f.key === this.scaleData()?.scoreConfig.scoreKey
    );
  }

  /**
   * @returns {number} The minimum possible score calculated from all fields.
   */
  get minScore(): number {
    if (!this.scaleData()) return 0;

    return this.scaleData().sections.reduce((minSum, section) => {
      return (
        minSum +
        section.fields.reduce((fieldSum, field) => fieldSum + field.min, 0)
      );
    }, 0);
  }

  constructor() {
    effect(() => {
      const data = this.scaleData();
      if (data && this.scaleForm) {
        this.setupCalculations(data);
      }
    });
  }

  /**
   * Subscribes to form value changes to trigger sequential calculations.
   */
  private setupCalculations(data: ScaleDefinition): void {
    this.scaleForm.valueChanges
      .pipe(untilDestroyed(this), startWith(this.scaleForm.value))
      .subscribe(() => this.calculateTotalScore(data));
  }

  /**
   * Evaluates all computed fields and patches the form with the results.
   */
  private calculateTotalScore(data: ScaleDefinition): void {
    if (!data.computedFields || data.computedFields.length === 0) return;

    const currentValues = { ...this.scaleForm.getRawValue() };

    data.computedFields.forEach(compField => {
      if (!compField.calculation || compField.type === 'list') return;

      try {
        const result = DynamicScaleCalculator.evaluateCalculation(
          compField.calculation,
          data,
          currentValues
        );

        const control = this.scaleForm.get(compField.key);
        if (control && control.value !== result) {
          control.setValue(result, { emitEvent: false });
        }

        currentValues[compField.key] = result;
      } catch (e) {
        console.error(`Error calculating field ${compField.key}:`, e);
      }
    });
  }

  /**
   * @returns {number} The current value of the field from the form.
   */
  getFieldValue(field: FieldDefinition): number {
    const val = this.scaleForm?.get(field.key)?.value;
    return val ?? field.min;
  }

  /**
   * @returns {string | undefined} The textual description linked to the current field value.
   */
  getValueDescription(field: FieldDefinition): string | undefined {
    if (!field.valueDescriptions) return undefined;
    const currentVal = this.getFieldValue(field);
    return field.valueDescriptions[currentVal];
  }

  /**
   * Enforces numeric ordering for KeyValue pipes in the template.
   */
  numericOrder = (
    a: KeyValue<string, unknown>,
    b: KeyValue<string, unknown>
  ): number => {
    return parseFloat(a.key) - parseFloat(b.key);
  };

  /**
   * @returns {number} The total number of steps allowed in the slider.
   */
  getSliderSteps(field: FieldDefinition): number {
    return field.allowedValues ? field.allowedValues.length - 1 : field.max;
  }

  /**
   * @returns {number} The active UI index corresponding to the real form value.
   */
  getSliderCurrentIndex(field: FieldDefinition): number {
    const realValue = this.scaleForm.get(field.key)?.value ?? field.min;

    if (field.allowedValues) {
      const idx = field.allowedValues.indexOf(realValue);
      return idx >= 0 ? idx : 0;
    }
    return realValue;
  }

  /**
   * Triggers an update to the form control when the slider index changes.
   */
  onSliderChange(field: FieldDefinition, sliderIndex: number): void {
    const realValue = field.allowedValues
      ? field.allowedValues[sliderIndex]
      : sliderIndex;
    this.scaleForm.get(field.key)?.setValue(realValue, { emitEvent: true });
  }

  /**
   * @returns {string} Formats the value shown on the slider tooltip.
   */
  formatLabel(field: FieldDefinition): (value: number) => string {
    return (sliderIndex: number) => {
      if (field.allowedValues) {
        return `${field.allowedValues[sliderIndex]}`;
      }
      return `${sliderIndex}`;
    };
  }

  /**
   * @returns {number} The minimum visual point of the slider.
   */
  getSliderMin(field: FieldDefinition): number {
    return field.allowedValues ? 0 : field.min;
  }

  /**
   * @returns {number} The maximum visual point of the slider.
   */
  getSliderMax(field: FieldDefinition): number {
    return field.allowedValues ? field.allowedValues.length - 1 : field.max;
  }
}
